import { Film, Image as ImageIcon, Play } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  autoAnimate,
  buildGalleryDoc,
  findGalleryEntry,
  label,
  MOTION_STYLE_MAP,
} from '@bannermatic/core';
import { Scene } from '@bannermatic/scene';
import { resolvePreviewSrc, useBuiltinFonts } from '../../components/DocPreview';
import { PlayingDoc } from '../../components/PlayingDoc';
import { cn } from '../../lib/format';
import { useLocale, useT } from '../../lib/i18n';

const SHOWCASE_TEMPLATE = 'summer-sale';
const STYLES = ['smooth', 'energetic', 'sale', 'kinetic', 'elegant'] as const;
type StyleId = (typeof STYLES)[number];

/**
 * A static banner turned into an animation with one click: the same "Animate" (autoAnimate) as in
 * the editor, played live with the motion player.
 */
export function MotionShowcase() {
  const t = useT();
  const locale = useLocale((s) => s.locale);
  useBuiltinFonts();
  const base = useMemo(
    () => buildGalleryDoc(findGalleryEntry(SHOWCASE_TEMPLATE)!, locale),
    [locale],
  );
  const [style, setStyle] = useState<StyleId | null>(null);
  const animated = useMemo(() => (style ? autoAnimate(base, style) : null), [base, style]);

  // Animate on its own once the block scrolls into view, unless the visitor got there first
  const root = useRef<HTMLDivElement>(null);
  const touched = useRef(false);
  useEffect(() => {
    const el = root.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        timer = setTimeout(() => {
          if (!touched.current) setStyle('smooth');
        }, 1400);
      },
      { threshold: 0.5 },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      clearTimeout(timer);
    };
  }, []);
  const pick = (s: StyleId | null) => {
    touched.current = true;
    setStyle(s);
  };

  // Fit the square banner into the stage
  const stage = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState(360);
  useEffect(() => {
    const el = stage.current;
    if (!el) return;
    const ro = new ResizeObserver(() =>
      setBox(Math.max(160, Math.min(el.clientWidth, el.clientHeight) - 48)),
    );
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const scale = Math.min(box / base.width, box / base.height);

  return (
    <div
      ref={root}
      className="overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 shadow-fuchsia-500/10 ring-zinc-200"
      data-testid="motion-showcase"
    >
      {/* Toolbar: static / animated and the style */}
      <div className="flex flex-wrap items-center gap-2 border-b border-zinc-200 bg-zinc-50 px-4 py-2.5">
        <div className="flex gap-0.5 rounded-lg bg-zinc-200/70 p-0.5">
          {(
            [
              [false, ImageIcon, 'motionShowcase.static'],
              [true, Film, 'motionShowcase.animated'],
            ] as const
          ).map(([on, Icon, key]) => (
            <button
              key={key}
              type="button"
              onClick={() => pick(on ? (style ?? 'smooth') : null)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition',
                !!style === on
                  ? 'bg-white text-zinc-900 shadow-sm'
                  : 'text-zinc-500 hover:text-zinc-800',
              )}
            >
              <Icon className="size-3.5" /> {t(key)}
            </button>
          ))}
        </div>
        {!style && (
          <button
            type="button"
            onClick={() => pick('smooth')}
            className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-brand-700"
            data-testid="motion-showcase-animate"
          >
            <Play className="size-3.5" /> {t('motion.animate')}
          </button>
        )}
        {style && (
          <span className="ml-auto text-[11px] font-medium text-zinc-400">
            {t('motionShowcase.formats')}
          </span>
        )}
      </div>

      {/* Stage */}
      <div
        ref={stage}
        className="relative flex h-[340px] items-center justify-center bg-zinc-100 sm:h-[440px]"
        style={{
          backgroundImage: 'radial-gradient(#d4d4d8 1px, transparent 1px)',
          backgroundSize: '18px 18px',
        }}
      >
        <div className="shadow-xl">
          {animated ? (
            <PlayingDoc doc={animated} box={box} />
          ) : (
            <div
              className="relative overflow-hidden"
              style={{ width: base.width * scale, height: base.height * scale }}
            >
              <div
                className="pointer-events-none absolute top-0 left-0"
                style={{
                  width: base.width,
                  height: base.height,
                  transform: `scale(${scale})`,
                  transformOrigin: '0 0',
                }}
              >
                <Scene doc={base} mode="render" resolveSrc={resolvePreviewSrc} />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Styles */}
      <div className="flex flex-wrap items-center gap-1.5 border-t border-zinc-200 px-4 py-3">
        <span className="mr-1 text-[11px] font-semibold tracking-wider text-zinc-400 uppercase">
          {t('motion.style')}
        </span>
        {STYLES.map((id) => {
          const s = MOTION_STYLE_MAP.get(id);
          if (!s) return null;
          return (
            <button
              key={id}
              type="button"
              title={label(s.description, locale)}
              onClick={() => pick(id)}
              className={cn(
                'rounded-full px-3 py-1 text-xs font-medium ring-1 transition',
                id === style
                  ? 'bg-fuchsia-600 text-white ring-fuchsia-600'
                  : 'bg-white text-zinc-600 ring-zinc-200 hover:ring-zinc-300',
              )}
            >
              {label(s.label, locale)}
            </button>
          );
        })}
      </div>
    </div>
  );
}
